import '@fontsource/onest/400.css';
import '@fontsource/onest/500.css';
import '@fontsource/onest/600.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './app.css';

import { Center, Loader, MantineProvider, Text } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { ReactElement, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Dashboard } from './dashboard/Dashboard';
import { ExpensesPage } from './pages/ExpensesPage';
import { LoginPage } from './pages/LoginPage';
import { FirstPasswordPage } from './pages/FirstPasswordPage';
import { MetricsPage } from './pages/MetricsPage';
import { PlansPage } from './pages/PlansPage';
import { CatalogsPage } from './pages/CatalogsPage';
import { SourcesPage } from './pages/SourcesPage';
import { AuditPage } from './pages/AuditPage';
import { UsersPage } from './pages/UsersPage';
import { navFor } from './nav';
import { SessionProvider, useSession } from './session';
import { theme } from './theme';

/**
 * dash.<домен клиники>: в корне — дашборд собственника без меню МИС, экраны МИС — под /mis
 * (один адрес на всё). На других адресах МИС в корне, дашборд — /dashboard.
 */
const DASH_HOST = window.location.hostname.startsWith('dash.');
const MIS_BASE = DASH_HOST ? '/mis' : '';
const IN_MIS = !DASH_HOST || window.location.pathname === MIS_BASE || window.location.pathname.startsWith(`${MIS_BASE}/`);

function App() {
  const { me, loading } = useSession();
  const { pathname } = useLocation();
  if (loading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (!me) return <LoginPage />;
  if (me.must_change_password) return <FirstPasswordPage />;
  if (!IN_MIS) return <Dashboard standalone misHref={`${MIS_BASE}/`} />;
  const allowed = navFor(me).map((n) => n.to);
  if (pathname.startsWith('/dashboard') && allowed.includes('/dashboard')) return <Dashboard standalone={false} misHref={`${MIS_BASE}/`} />;
  const home = allowed[0];
  const page = (to: string, el: ReactElement) => allowed.includes(to) && <Route path={to} element={el} />;
  return (
    <Layout>
      {home ? (
        <Routes>
          {page('/expenses', <ExpensesPage />)}
          {page('/metrics', <MetricsPage />)}
          {page('/plans', <PlansPage />)}
          {page('/catalogs', <CatalogsPage />)}
          {page('/sources', <SourcesPage />)}
          {page('/audit', <AuditPage />)}
          {page('/users', <UsersPage />)}
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      ) : (
        <Center h="60vh" p="md">
          <Text c="dimmed" ta="center" maw={420}>
            Для вашей роли в МИС нет разделов: отчёты открываются в дашборде. Если нужен ввод затрат или показателей — обратитесь к администратору.
          </Text>
        </Center>
      )}
    </Layout>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <DatesProvider settings={{ locale: 'ru', firstDayOfWeek: 1 }}>
        <Notifications position="bottom-center" />
        <ModalsProvider>
          <BrowserRouter basename={IN_MIS ? MIS_BASE : undefined}>
            <SessionProvider>
              <App />
            </SessionProvider>
          </BrowserRouter>
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  </StrictMode>,
);
